import { JOB_MESSAGE_KINDS, JOB_MESSAGE_KIND_LABELS, type JobMessageKind } from '../documents/jobMessages';
import { siteTypePath } from './siteTypes';

/**
 * 🔒 A site type's defaults (piece 10, piece 15; decision 10210; T3514; the owner's answers Q1c and Q6a of 2026-10-29): the
 * site requirement types a site of the type starts with, an On or Off per Work Order message kind, and the Tax exempt
 * customer flag. A new site of the type takes all three on save; on an existing site a type change fills what was never
 * set and asks Overwrite or Keep for each setting that is set and differs. 🔨 Nothing set is overwritten without an answer.
 * These are the rules and the words that need no database. Every path is relative to the API root.
 */

/** 🔒 The right to change a site's Tax exempt customer flag, by hand or by a type's default (decision 10210; Q6a). */
export const SITE_TAX_EXEMPT_CAPABILITY = 'site.taxExempt.set';

/** 🔒 The refusal of a flag change by a person without the permission, naming it. */
export const SITE_TAX_EXEMPT_REFUSAL = `Changing Tax exempt customer needs the ${SITE_TAX_EXEMPT_CAPABILITY} permission. An administrator grants it on Settings › People › Roles.`;

/** 🔒 The refusal of a type default that would change the flag, for a person without the permission. */
export function siteTypeFlagDefaultRefusal(typeName: string): string {
  return `${typeName} makes its sites tax exempt customers, and changing Tax exempt customer needs the ${SITE_TAX_EXEMPT_CAPABILITY} permission. An administrator grants it on Settings › People › Roles.`;
}

/** 🔒 The three settings a type defaults, in the order the windows draw them. */
export const SITE_TYPE_DEFAULT_SETTINGS = ['requirements', 'messages', 'taxExempt'] as const;
export type SiteTypeDefaultSetting = (typeof SITE_TYPE_DEFAULT_SETTINGS)[number];

/** 🔒 The two answers a set setting takes, the owner's own: overwrite it, or keep (maintain) what the site has. */
export const SITE_TYPE_DEFAULT_ANSWERS = ['overwrite', 'keep'] as const;
export type SiteTypeDefaultAnswer = (typeof SITE_TYPE_DEFAULT_ANSWERS)[number];
export type SiteTypeDefaultAnswers = Partial<Record<SiteTypeDefaultSetting, SiteTypeDefaultAnswer>>;

/** One message kind a type switches on or off. */
export interface SiteTypeMessageDefaultRow {
  kind: JobMessageKind;
  isEnabled: boolean;
}

/** What the Defaults window sends: the requirement types, the message kinds with a default, and whether the type flags its sites. */
export interface SiteTypeDefaultsDraft {
  requirementTypeIds: string[];
  messages: SiteTypeMessageDefaultRow[];
  taxExempt: boolean;
}

/** What the Defaults window reads: the type's defaults, its version, and the requirement types it may choose from. */
export interface SiteTypeDefaultsAnswer extends SiteTypeDefaultsDraft {
  siteTypeId: string;
  name: string;
  version: number;
  /** The active requirement types, and any retired one the type still names, by name. */
  requirementTypes: { id: string; name: string; isActive: boolean }[];
}

/** 🔒 The type's defaults, read and saved by the Settings page behind `admin.operations`. */
export function siteTypeDefaultsPath(siteTypeId: string): string {
  return `${siteTypePath(siteTypeId)}/defaults`;
}

/**
 * 🔒 What a site has now and what a type offers, setting by setting, under the Site module's read. `taxExempt` is the Edit
 * form's pending flag (T3522): the same Save writes it before the defaults apply, so the offer compares it and not the stored one.
 */
export function siteTypeOfferPath(siteId: string, siteTypeId: string, taxExempt?: boolean): string {
  return `/pages/sites/${encodeURIComponent(siteId)}/type-defaults?siteTypeId=${encodeURIComponent(siteTypeId)}${taxExempt === undefined ? '' : `&taxExempt=${taxExempt ? 'true' : 'false'}`}`;
}

/**
 * How a setting meets a type change: `none` where the type offers nothing, `same` where the site already has it, `fill`
 * where the site never set it, and `ask` where it is set and differs.
 */
export type SiteTypeOfferState = 'none' | 'same' | 'fill' | 'ask';

/** One setting as the answer window draws it: what the site has now and what the type offers, in words. */
export interface SiteTypeOfferRow {
  setting: SiteTypeDefaultSetting;
  state: SiteTypeOfferState;
  now: string[];
  offered: string[];
  /** Why Overwrite cannot be chosen by this person, in the server's sentence; null where it can. */
  blocked: string | null;
}

export interface SiteTypeOfferAnswer {
  siteId: string;
  siteTypeId: string;
  typeName: string;
  rows: SiteTypeOfferRow[];
}

/** A site's own requirement row, as the offer compares it. */
export interface HeldRequirement {
  siteRequirementTypeId: string;
  required: boolean;
}

/**
 * 🔒 The requirements' state (decision 10210): never set where the site has no running row of its own, the same where its
 * own rows require exactly the offered types and waive none, and asked otherwise.
 */
export function requirementsOfferState(held: readonly HeldRequirement[], offered: readonly string[]): SiteTypeOfferState {
  if (offered.length === 0) return 'none';
  if (held.length === 0) return 'fill';
  const required = new Set(held.filter((row) => row.required).map((row) => row.siteRequirementTypeId));
  const waives = held.some((row) => !row.required);
  const same = !waives && required.size === new Set(offered).size && offered.every((id) => required.has(id));
  return same ? 'same' : 'ask';
}

/** The message kinds a type change fills without asking, and those it asks about, against the customer's own rules. */
export function messagesOffer(rules: ReadonlyMap<JobMessageKind, boolean>, offered: readonly SiteTypeMessageDefaultRow[]): { state: SiteTypeOfferState; fill: JobMessageKind[]; differ: JobMessageKind[] } {
  const unset = offered.filter((row) => !rules.has(row.kind)).map((row) => row.kind);
  const differ = offered.filter((row) => rules.has(row.kind) && rules.get(row.kind) !== row.isEnabled).map((row) => row.kind);
  const state: SiteTypeOfferState = offered.length === 0 ? 'none' : differ.length > 0 ? 'ask' : unset.length > 0 ? 'fill' : 'same';
  return { state, fill: unset, differ };
}

/** 🔨 The flag always counts as set (decision 10210): a site that is not a tax exempt customer cannot be told from one nobody looked at. */
export function flagOfferState(held: boolean, offered: boolean | null): SiteTypeOfferState {
  if (offered === null) return 'none';
  return held === offered ? 'same' : 'ask';
}

/** The settings of an offer that ask, in the window's order. */
export function askingRows(answer: SiteTypeOfferAnswer | null): SiteTypeOfferRow[] {
  return answer ? answer.rows.filter((row) => row.state === 'ask') : [];
}

/** 🔨 Apply is held until every asking row has an answer. */
export function answersComplete(rows: readonly SiteTypeOfferRow[], answers: SiteTypeDefaultAnswers): boolean {
  return rows.every((row) => answers[row.setting] === 'overwrite' || answers[row.setting] === 'keep');
}

/** A message kind's default in words, as both windows read it. */
export function messageDefaultWords(row: SiteTypeMessageDefaultRow): string {
  return `${JOB_MESSAGE_KIND_LABELS[row.kind]}: ${row.isEnabled ? 'On' : 'Off'}`;
}

/** 🔒 The answers a Site write carries, read from its body; anything else is refused before the write, in a sentence. */
export function applyDefaultsFrom(value: unknown): SiteTypeDefaultAnswers | string {
  if (value === undefined || value === null) return {};
  if (typeof value !== 'object' || Array.isArray(value)) return 'The type defaults answer Overwrite or Keep for each setting.';
  const answers: SiteTypeDefaultAnswers = {};
  for (const [key, answer] of Object.entries(value as Record<string, unknown>)) {
    if (!(SITE_TYPE_DEFAULT_SETTINGS as readonly string[]).includes(key)) return `${key} is not a setting a site type defaults.`;
    if (answer !== 'overwrite' && answer !== 'keep') return 'The type defaults answer Overwrite or Keep for each setting.';
    answers[key as SiteTypeDefaultSetting] = answer;
  }
  return answers;
}

/** 🔒 The Defaults save's own check: whole lists, each kind once, and a flag that is true or false; null passes. */
export function siteTypeDefaultsProblem(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return 'The defaults name the requirement types, the message kinds and the flag.';
  const draft = body as Partial<Record<keyof SiteTypeDefaultsDraft, unknown>>;
  if (!Array.isArray(draft.requirementTypeIds) || draft.requirementTypeIds.some((id) => typeof id !== 'string')) return 'The requirement types are a list of site requirement types.';
  if (!Array.isArray(draft.messages)) return 'The message defaults are a list of message kinds, each On or Off.';
  const kinds = new Set<string>();
  for (const row of draft.messages as unknown[]) {
    const kind = (row as { kind?: unknown } | null)?.kind;
    const isEnabled = (row as { isEnabled?: unknown } | null)?.isEnabled;
    if (typeof kind !== 'string' || !(JOB_MESSAGE_KINDS as readonly string[]).includes(kind) || typeof isEnabled !== 'boolean') return 'The message defaults are a list of message kinds, each On or Off.';
    if (kinds.has(kind)) return `${kind} is named twice in the message defaults.`;
    kinds.add(kind);
  }
  if (typeof draft.taxExempt !== 'boolean') return 'Tax exempt customer is ticked or not.';
  return null;
}
